import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import type {
  AssignmentCoverImage,
  AssignmentSubTask,
  ProjectAssignmentItem,
} from '../../types/assignmentPointsNote';
import type { ItemCard } from '../../types/pointItemNote';
import {
  pruneProjectStudentStatuses,
  projectEditWouldDeleteRecords,
} from '../../utils/assignmentEditCleanup';
import AssignmentCoverPickField from './AssignmentCoverPickField';
import AssignmentMemberPickField from './AssignmentMemberPickField';
import AssignmentRecordDeleteConfirm from './AssignmentRecordDeleteConfirm';
import AssignmentRewardItemPickModal from './AssignmentRewardItemPickModal';
import type { FlatMember } from './GeneralAssignmentSettingsModal';
import { resolveItemCardImageSrc } from '../../utils/itemCardImage';
import {
  copyTitle,
  CreateDateField,
  CreateRewardCoinField,
  newId,
  todayIso,
} from './assignmentCreateModalShared';

const LEVEL_COLORS = [
  '#FC5555',
  '#FF9933',
  '#669900',
  '#119473',
  '#0066CC',
  '#9900CC',
  '#D60093',
  '#920049',
  '#CCCC00',
  '#565050',
];

function lightenColor(hex: string, brightness = 0.6): string {
  const clean = hex.replace('#', '');
  const r = parseInt(clean.slice(0, 2), 16);
  const g = parseInt(clean.slice(2, 4), 16);
  const b = parseInt(clean.slice(4, 6), 16);
  const nr = Math.round(r * (1 - brightness) + 255 * brightness);
  const ng = Math.round(g * (1 - brightness) + 255 * brightness);
  const nb = Math.round(b * (1 - brightness) + 255 * brightness);
  return `#${nr.toString(16).padStart(2, '0')}${ng.toString(16).padStart(2, '0')}${nb
    .toString(16)
    .padStart(2, '0')}`;
}

const PROJECT_COLORS = [...LEVEL_COLORS, ...LEVEL_COLORS.map((c) => lightenColor(c, 0.6))];

type Props = {
  open: boolean;
  mode: 'new' | 'edit' | 'copy';
  initial?: ProjectAssignmentItem | null;
  members: FlatMember[];
  defaultGroupId?: string | null;
  defaultPoints: number;
  /** 포인트·아이템 노트의 과제보상 아이템 목록 */
  assignmentRewardItems?: ItemCard[];
  coverImages: AssignmentCoverImage[];
  onCoverImagesChange: (images: AssignmentCoverImage[]) => void;
  onClose: () => void;
  onSave: (item: ProjectAssignmentItem) => void;
  /** 챌린지 과제: 하위 챌린지는 챌린지 편집 모달에서 구성(여기서는 기본 설정만) */
  challengeMode?: boolean;
};

function SortableSubTaskRow({
  subTask,
  order,
  pointStep,
  onChange,
  onRemove,
  canRemove,
}: {
  subTask: AssignmentSubTask;
  order: number;
  pointStep: number;
  onChange: (patch: Partial<AssignmentSubTask>) => void;
  onRemove: () => void;
  canRemove: boolean;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: subTask.id,
  });
  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.7 : 1,
  };
  const step = pointStep > 0 ? pointStep : 1;

  return (
    <div ref={setNodeRef} style={style} className="cp-apn-create-subtable-row">
      <span className="cp-apn-create-subtable-ord">{order}</span>
      <input
        className="cp-apn-create-input"
        value={subTask.title}
        placeholder="하위 과제 제목"
        onChange={(e) => onChange({ title: e.target.value })}
      />
      <input
        className="cp-apn-create-input cp-apn-create-subtasks-pts"
        type="number"
        min={0}
        step={step}
        value={subTask.points}
        onChange={(e) => onChange({ points: Number(e.target.value) || 0 })}
        onKeyDown={(e) => {
          if (e.key === 'ArrowUp') {
            e.preventDefault();
            onChange({ points: (Number(subTask.points) || 0) + step });
          } else if (e.key === 'ArrowDown') {
            e.preventDefault();
            onChange({ points: Math.max(0, (Number(subTask.points) || 0) - step) });
          }
        }}
      />
      <button
        type="button"
        className="cp-apn-create-subtable-icon-btn is-danger"
        disabled={!canRemove}
        aria-label="삭제"
        onClick={onRemove}
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden>
          <path d="M3 6h18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          <path
            d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
          />
        </svg>
      </button>
      <button
        type="button"
        className="cp-apn-create-subtable-icon-btn is-drag"
        aria-label="이동"
        {...attributes}
        {...listeners}
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden>
          <path d="M4 7h16M4 12h16M4 17h16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        </svg>
      </button>
    </div>
  );
}

const ProjectAssignmentSettingsModal: React.FC<Props> = ({
  open,
  mode,
  initial,
  members,
  defaultGroupId = null,
  defaultPoints,
  assignmentRewardItems = [],
  coverImages,
  onCoverImagesChange,
  onClose,
  onSave,
  challengeMode = false,
}) => {
  const [title, setTitle] = useState('');
  const [dueDate, setDueDate] = useState(todayIso());
  const [color, setColor] = useState('#FC5555');
  const [colorOpen, setColorOpen] = useState(false);
  const [notes, setNotes] = useState('');
  const [coverImageId, setCoverImageId] = useState<string | undefined>();
  const [subTasks, setSubTasks] = useState<AssignmentSubTask[]>([
    { id: newId('sub'), title: '', points: defaultPoints, order: 1 },
  ]);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [rewardEnabled, setRewardEnabled] = useState(false);
  const [rewardCoinsDisplay, setRewardCoinsDisplay] = useState('0');
  const [rewardItemIds, setRewardItemIds] = useState<string[]>([]);
  const [itemPickOpen, setItemPickOpen] = useState(false);
  const [pendingSave, setPendingSave] = useState<ProjectAssignmentItem | null>(null);

  const getRewardCoinsNum = () =>
    rewardCoinsDisplay === '' ? 0 : Number(rewardCoinsDisplay) || 0;

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  useEffect(() => {
    if (!open) return;
    setColorOpen(false);
    setItemPickOpen(false);
    setPendingSave(null);
    if ((mode === 'edit' || mode === 'copy') && initial) {
      setTitle(mode === 'copy' ? copyTitle(initial.title) : initial.title);
      setDueDate(initial.dueDate || todayIso());
      setColor(initial.color || '#FC5555');
      setNotes(initial.notes ?? '');
      setCoverImageId(initial.coverImageId);
      setSubTasks(
        initial.subTasks.length
          ? initial.subTasks.map((s, i) => ({
              ...s,
              id: mode === 'copy' ? newId('sub') : s.id,
              order: i + 1,
            }))
          : [{ id: newId('sub'), title: '', points: defaultPoints, order: 1 }]
      );
      setSelectedIds(new Set(initial.assignedMemberIds));
      setRewardEnabled(!!initial.rewardEnabled);
      setRewardCoinsDisplay(String(initial.rewardCoins ?? 0));
      setRewardItemIds([...(initial.rewardItemIds ?? [])]);
    } else {
      setTitle('');
      setDueDate(todayIso());
      setColor('#FC5555');
      setNotes('');
      setCoverImageId(undefined);
      setSubTasks([
        { id: newId('sub'), title: challengeMode ? '인증과제 1' : '', points: defaultPoints, order: 1 },
      ]);
      const defaultMembers = defaultGroupId
        ? members.filter((m) => m.groupId === defaultGroupId)
        : members;
      setSelectedIds(new Set(defaultMembers.map((m) => m.id)));
      setRewardEnabled(false);
      setRewardCoinsDisplay('0');
      setRewardItemIds([]);
    }
  }, [open, mode, initial, defaultPoints, members, defaultGroupId, challengeMode]);

  useEffect(() => {
    if (!open) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  const selectedRewardCards = useMemo(() => {
    const map = new Map(assignmentRewardItems.map((c) => [c.itemCardId, c]));
    return rewardItemIds
      .map((id) => map.get(id))
      .filter((c): c is ItemCard => c != null);
  }, [assignmentRewardItems, rewardItemIds]);

  const canSave =
    title.trim().length > 0 &&
    !!dueDate &&
    subTasks.length > 0 &&
    (challengeMode || subTasks.every((s) => s.title.trim())) &&
    selectedIds.size > 0;

  const heading = challengeMode
    ? mode === 'edit'
      ? '챌린지 프로젝트 설정'
      : mode === 'copy'
        ? '챌린지 과제 복제'
        : '챌린지 과제 생성'
    : mode === 'edit'
      ? '프로젝트 과제 수정'
      : mode === 'copy'
        ? '복제 과제 만들기'
        : '프로젝트 과제 생성';

  if (!open) return null;

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setSubTasks((prev) => {
      const oldIndex = prev.findIndex((s) => s.id === active.id);
      const newIndex = prev.findIndex((s) => s.id === over.id);
      if (oldIndex < 0 || newIndex < 0) return prev;
      return arrayMove(prev, oldIndex, newIndex).map((s, i) => ({ ...s, order: i + 1 }));
    });
  };

  const handleSave = () => {
    if (!canSave) return;
    const now = new Date().toISOString();
    const assignedMemberIds = members.filter((m) => selectedIds.has(m.id)).map((m) => m.id);
    const nextSubs = subTasks.map((s, i) => ({
      ...s,
      title: s.title.trim() || (challengeMode ? `인증과제 ${i + 1}` : ''),
      points: Number(s.points) || 0,
      order: i + 1,
    }));
    const studentStatusesBySubTask = pruneProjectStudentStatuses(
      mode === 'edit' && initial ? initial.studentStatusesBySubTask : {},
      nextSubs,
      assignedMemberIds
    );
    const nextItem: ProjectAssignmentItem = {
      id: mode === 'edit' && initial ? initial.id : newId('prj'),
      kind: 'project',
      title: title.trim(),
      dueDate,
      color,
      notes: notes.trim() || undefined,
      coverImageId,
      rewardEnabled,
      rewardCoins: rewardEnabled ? getRewardCoinsNum() : 0,
      rewardItemIds: rewardEnabled ? rewardItemIds : [],
      rewardGrantedStudentIds:
        mode === 'edit' && initial ? initial.rewardGrantedStudentIds : undefined,
      assignedMemberIds,
      subTasks: nextSubs,
      studentStatusesBySubTask,
      ...(mode === 'edit' && initial?.challenge ? { challenge: initial.challenge } : {}),
      createdAt: mode === 'edit' && initial ? initial.createdAt : now,
      updatedAt: now,
    };
    if (mode === 'edit' && initial && projectEditWouldDeleteRecords(initial, nextItem)) {
      setPendingSave(nextItem);
      return;
    }
    onSave(nextItem);
  };

  return createPortal(
    <div className="cp-apn-create-overlay" role="presentation">
      <div
        className="cp-apn-create-overlay-dim"
        aria-hidden
        onClick={onClose}
      />
      <div
        className="cp-apn-create-overlay-scroll"
        role="presentation"
        onClick={onClose}
      >
        <div
          className="cp-apn-create-modal"
          role="dialog"
          aria-modal="true"
          aria-label={heading}
          onClick={(e) => e.stopPropagation()}
        >
        <h2 className="cp-apn-create-title">{heading}</h2>

        <AssignmentCoverPickField
          coverImageId={coverImageId}
          coverImages={coverImages}
          onCoverImageIdChange={setCoverImageId}
          onCoverImagesChange={onCoverImagesChange}
        >
          <div className="cp-apn-create-field">
            <label className="cp-apn-create-label">{challengeMode ? '챌린지 프로젝트 이름' : '과제 이름'}</label>
            <input
              className="cp-apn-create-input"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={challengeMode ? '챌린지 프로젝트 이름을 입력하세요' : '과제 이름을 입력하세요'}
            />
          </div>
          <div className="cp-apn-create-field">
            <label className="cp-apn-create-label">
              {challengeMode ? '챌린지 프로젝트 설명 (선택사항)' : '과제 설명 (선택사항)'}
            </label>
            <textarea
              className="cp-apn-create-textarea"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="과제에 대한 추가 설명이나 메모를 입력하세요..."
              rows={3}
            />
          </div>
        </AssignmentCoverPickField>

        <section
          className="cp-apn-create-section cp-apn-create-section--compact"
          data-tutorial-apn-create="reward"
        >
          <div className="cp-apn-create-toggle-row">
            <span className="cp-apn-create-label">
              {challengeMode ? '챌린지 프로젝트 완료 보상' : '프로젝트 완료 보상'}
            </span>
            <button
              type="button"
              className={`cp-apn-create-toggle${rewardEnabled ? ' is-on' : ''}`}
              aria-pressed={rewardEnabled}
              onClick={() => setRewardEnabled((v) => !v)}
            >
              <span className="cp-apn-create-toggle-knob">{rewardEnabled ? 'ON' : 'OFF'}</span>
            </button>
          </div>
          {rewardEnabled ? (
            <>
              <CreateRewardCoinField
                value={rewardCoinsDisplay}
                onChange={setRewardCoinsDisplay}
                getNum={getRewardCoinsNum}
              />
              <div className="cp-apn-create-field">
                <label className="cp-apn-create-label">
                  획득 카드 설정 (프로젝트 보상 아이템)
                </label>
                <div className="cp-apn-reward-cards">
                  {selectedRewardCards.map((card) => (
                    <div key={card.itemCardId} className="cp-apn-reward-card">
                      <div className="cp-apn-reward-card-thumb" aria-hidden>
                        {resolveItemCardImageSrc(card.cardImage) ? (
                          <img src={resolveItemCardImageSrc(card.cardImage)!} alt="" />
                        ) : (
                          <span>카드</span>
                        )}
                      </div>
                      <div className="cp-apn-reward-card-name">{card.name}</div>
                      {card.memo ? (
                        <div className="cp-apn-reward-card-memo">{card.memo}</div>
                      ) : null}
                      <button
                        type="button"
                        className="cp-apn-reward-card-remove"
                        aria-label="카드 제거"
                        onClick={() =>
                          setRewardItemIds((prev) =>
                            prev.filter((id) => id !== card.itemCardId)
                          )
                        }
                      >
                        ×
                      </button>
                    </div>
                  ))}
                  <button
                    type="button"
                    className="cp-apn-reward-card-add"
                    onClick={() => setItemPickOpen(true)}
                  >
                    <span className="cp-apn-reward-card-add-icon">+</span>
                    <span>아이템 카드 추가</span>
                  </button>
                </div>
              </div>
            </>
          ) : null}
        </section>

        <section className="cp-apn-create-section" data-tutorial-apn-create="details">
          <CreateDateField label="마감 날짜" value={dueDate} onChange={setDueDate} />

          {challengeMode ? (
            mode === 'new' ? (
              <p className="cp-chl-settings-note">
                저장하면 인증과제 편집 화면이 열립니다. 챌린지 내용·인증 항목·포인트는 그곳에서 실제 화면처럼 꾸밀 수 있어요.
              </p>
            ) : null
          ) : (
          <div className="cp-apn-create-field">
            <label className="cp-apn-create-label">하위 과제 설정</label>
            <div className="cp-apn-create-subtable">
              <div className="cp-apn-create-subtable-head">
                <span>순</span>
                <span>하위 과제 제목</span>
                <span>포인트</span>
                <span>삭제</span>
                <span>이동</span>
              </div>
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
              >
                <SortableContext
                  items={subTasks.map((s) => s.id)}
                  strategy={verticalListSortingStrategy}
                >
                  {subTasks.map((s, idx) => (
                    <SortableSubTaskRow
                      key={s.id}
                      subTask={s}
                      order={idx + 1}
                      pointStep={defaultPoints}
                      canRemove={subTasks.length > 1}
                      onChange={(patch) =>
                        setSubTasks((prev) =>
                          prev.map((x) => (x.id === s.id ? { ...x, ...patch } : x))
                        )
                      }
                      onRemove={() =>
                        setSubTasks((prev) => prev.filter((x) => x.id !== s.id))
                      }
                    />
                  ))}
                </SortableContext>
              </DndContext>
              <div className="cp-apn-create-subtable-add-wrap">
                <button
                  type="button"
                  className="cp-apn-create-subtable-add"
                  aria-label="하위 과제 추가"
                  onClick={() =>
                    setSubTasks((prev) => [
                      ...prev,
                      {
                        id: newId('sub'),
                        title: '',
                        points: defaultPoints,
                        order: prev.length + 1,
                      },
                    ])
                  }
                >
                  +
                </button>
              </div>
            </div>
          </div>
          )}

          <div className="cp-apn-create-field">
            <label className="cp-apn-create-label">{challengeMode ? '챌린지 프로젝트 색상' : '프로젝트 색상'}</label>
            <div className="cp-apn-create-color-picker">
              <button
                type="button"
                className="cp-apn-create-color-picker-btn"
                onClick={() => setColorOpen((v) => !v)}
                aria-expanded={colorOpen}
              >
                <span className="cp-apn-create-cat-dot" style={{ background: color }} />
                <span>프로젝트</span>
                <span className="cp-apn-create-cat-chevron" aria-hidden>
                  ▾
                </span>
              </button>
              {colorOpen ? (
                <>
                  <div
                    className="cp-apn-create-color-overlay"
                    role="presentation"
                    onClick={() => setColorOpen(false)}
                  />
                  <div className="cp-apn-create-color-menu">
                    <div className="cp-apn-create-color-grid">
                      {PROJECT_COLORS.map((c) => (
                        <button
                          key={c}
                          type="button"
                          className={`cp-apn-create-color-swatch${
                            color.toLowerCase() === c.toLowerCase() ? ' is-selected' : ''
                          }`}
                          style={{ background: c }}
                          aria-label={c}
                          onClick={() => {
                            setColor(c);
                            setColorOpen(false);
                          }}
                        />
                      ))}
                    </div>
                  </div>
                </>
              ) : null}
            </div>
          </div>
        </section>

        <section className="cp-apn-create-section" data-tutorial-apn-create="members">
          <AssignmentMemberPickField
            members={members}
            selectedIds={selectedIds}
            onChange={setSelectedIds}
            initialGroupId={defaultGroupId}
            educodiLayout
          />
        </section>

        <div className="cp-apn-create-buttons">
          <button type="button" className="cp-apn-create-btn-cancel" onClick={onClose}>
            취소
          </button>
          <button
            type="button"
            className="cp-apn-create-btn-save"
            disabled={!canSave}
            onClick={handleSave}
          >
            저장
          </button>
        </div>
        </div>
      </div>

      <AssignmentRecordDeleteConfirm
        open={pendingSave != null}
        kind="project"
        onCancel={() => setPendingSave(null)}
        onConfirm={() => {
          if (!pendingSave) return;
          onSave(pendingSave);
          setPendingSave(null);
        }}
      />

      <AssignmentRewardItemPickModal
        open={itemPickOpen}
        items={assignmentRewardItems}
        initialSelectedIds={rewardItemIds}
        onClose={() => setItemPickOpen(false)}
        onConfirm={(ids) => {
          setRewardItemIds(ids);
          setItemPickOpen(false);
        }}
      />
    </div>,
    document.body
  );
};

export default ProjectAssignmentSettingsModal;
